import { useMemo } from 'react';
import { normalizeCnjBaseDigits, parseCnjProcessNumber } from '../utils/cnjProcessNumber.js';

/**
 * Hook para automaticamente detectar e limpar números CNJ em buscas
 * @param {string} searchTerm - Termo de busca que pode conter número CNJ formatado
 * @returns {object} Objeto com termo limpo e informações sobre detecção CNJ
 */
export const useSearchCNJ = (searchTerm) => {
  const result = useMemo(() => {
    if (!searchTerm) {
      return {
        cleanedTerm: '',
        isCNJNumber: false,
        originalTerm: searchTerm,
        digitsOnly: '',
        hasIncident: false,
      };
    }

    const parsed = parseCnjProcessNumber(searchTerm);
    const baseDigits = normalizeCnjBaseDigits(searchTerm);
    const isCNJNumber = baseDigits.length === 20;

    return {
      cleanedTerm: isCNJNumber ? baseDigits : searchTerm,
      isCNJNumber,
      originalTerm: searchTerm,
      digitsOnly: baseDigits,
      hasIncident: parsed?.hasIncident ?? false,
    };
  }, [searchTerm]);

  return result;
};

/**
 * Função utilitária para limpar números CNJ (sem hook)
 * @param {string} text - Texto que pode conter número CNJ formatado
 * @returns {string} Texto limpo (base CNJ se válido, original caso contrário)
 */
export const cleanCNJForSearch = (text) => {
  if (!text) return text;

  const baseDigits = normalizeCnjBaseDigits(text);
  if (baseDigits.length === 20) {
    return baseDigits;
  }

  return text;
};

/**
 * Função para detectar se um texto contém um número CNJ
 * @param {string} text - Texto a ser verificado
 * @returns {boolean} True se contém número CNJ válido
 */
export const isCNJNumber = (text) => {
  if (!text) return false;
  return normalizeCnjBaseDigits(text).length === 20;
};
